import { events } from "@constants/tracking";
import { useSessionConfigDialog } from "@contexts/DialogContext/dialogs/SessionConfigDialog";
import { usePurchaseDialog } from "@contexts/DialogContext/dialogs/purchase";
import CollectUserDetailsDialog from "@features/account/components/dialogs/CollectUserDetailsDialog";
import FreeQuizSessionDialog from "@features/auth/components/dialogs/FreeQuizSessionDialog";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import useMultipleExams from "@features/exam/contexts/useMultipleExams";
import { AppReviewDialog } from "@features/feedback/components/dialogs/AppReviewDialog";
import { getShowStudyPackViews } from "@helpers/exams";
import { useAppStore } from "@hooks/useAppStore";
import { isWeb } from "@memorang/helpers/platform";
import { resolveExamDefaultProductType } from "@memorang/helpers/product";
import BottomFab from "@memorang/ui/components/BottomFab";
import Box from "@memorang/ui/components/Box";
import Container from "@memorang/ui/components/Container";
import { HomeApplet } from "@memorang/ui/components/HomeApplet";
import { OLXExamEligibilityDialog } from "@memorang/ui/components/OLXExamEligibilityDialog";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { useDevice } from "@memorang/ui/useDevice";
import { trackCustomEvent } from "analytics";
import SetExamDateContainer from "components/SetExamDateContainer";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AppContextBannerContainer from "../containers/AppContextBannerContainer";
import ItemTypeDetailModalContainer from "../containers/ItemTypeDetailModalContainer";
import { NestedExamChipsContainer } from "../containers/NestedExamChipsContainer";
import SkillDetailDialogContainer from "../containers/SkillDetailDialogContainer";
import {
	PracticeTestsContextProvider,
	usePracticeTestsContext,
} from "../contexts/PracticeTestsContext";
import { useHomeHeader } from "../hooks/useHomeHeader";
import { useHomeSections } from "../hooks/useHomeSections";
import { useRecommendedSessionDialog } from "../hooks/useRecommendedSessionDialog";
import { AppBar } from "../layouts/AppBar";

export const HomeRouteContainer = () => (
	<PracticeTestsContextProvider>
		<HomeRouteContent />
	</PracticeTestsContextProvider>
);

const HomeRouteContent = () => {
	const {
		currentExamContext: { studyPackCollectionId, examName },
	} = useExamContext();
	const appFont = useAppStore((state) => state.app.font);

	const { isMobile } = useDevice();
	const { t } = useTranslation();

	const { startDiagnostic } = useLocalSearchParams<{
		startDiagnostic: string;
	}>();

	const { relatedExams } = useMultipleExams();

	const router = useRouter();

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const [showDatePicker, setShowDatePicker] = useState(false);
	const [detailQuestionTypeId, setDetailQuestionTypeId] = useState<string>();
	const [detailSkillId, setDetailSkillId] = useState<string>();

	const toggleShowDatePicker = () => {
		setShowDatePicker((prev) => !prev);
	};

	const {
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		olxError,
		setOlxError,
	} = usePracticeTestsContext();

	const { showPurchase } = usePurchaseDialog();
	const { showSessionConfig } = useSessionConfigDialog();
	const {
		openRecommendedSession,
		recommendedSessionDialog,
		startAdaptiveSession,
		isCreatingAdaptiveSession,
	} = useRecommendedSessionDialog("recommended_tasks");

	const handleSelectTags = (tagIds: string[]) => {
		showSessionConfig({ selectedTagIds: tagIds.map(Number) });
	};

	const { sections } = useHomeSections({
		onSelectTags: handleSelectTags,
		onStartRecommendedSession: openRecommendedSession,
	});

	const { tip, statsLoading, ...examInfo } = useHomeHeader();

	const statsWithPress = examInfo.stats?.map((stat) => ({
		...stat,
		onPress: stat.skillId
			? () => {
					trackCustomEvent({
						eventName: events.skillTilePressed,
						surface: "home",
						skill: stat.label,
						tileState: stat.tileState,
					});
					setDetailSkillId(stat.skillId);
				}
			: undefined,
	}));

	const handleQuickStudy = () => {
		trackCustomEvent({
			eventName: events.studyFabClicked,
		});
		router.push("/session/customize");
	};

	const showBottomFab = isMobile && showStudyPackViews;

	const showFreeQuizDialog = startDiagnostic === "true";

	const handleShowPurchase = () => {
		showPurchase({
			productType: resolveExamDefaultProductType(studyPackCollectionId),
			fromHomePage: true,
		});
	};

	const filteredSections = sections.filter(
		(section) => section.isLoading || section.data[0]?.items.length > 0,
	);

	return (
		<Box position="relative" flexGrow={1} height={"100%"}>
			<AppBar title={t("appbar.home.title")} />
			<Box flex={1}>
				<AppContextBannerContainer />
				{relatedExams.length > 0 && (
					<Container
						maxWidth="sm"
						autoSize
						containerStyle={
							isMobile
								? {
										paddingVertical: 16,
									}
								: {
										paddingTop: 24,
										paddingHorizontal: 8,
									}
						}
					>
						<NestedExamChipsContainer />
					</Container>
				)}
				<ScrollContainer
					maxWidth="sm"
					contentContainerStyle={
						isMobile
							? {
									paddingBottom: 96,
								}
							: {
									padding: 24,
								}
					}
				>
					<HomeApplet
						value={{
							header: {
								examInfo: {
									...examInfo,
									stats: statsWithPress,
									statsLoading,
									toggleShowDatePicker,
								},
								tip,
							},
							sections: filteredSections,
							options: {
								appFont,
								showStudyPackViews,
								studyPackSubtitle: examName,
								onNavigate: (link) => router.push(link as never),
								onSelectTopic: (topicId) => handleSelectTags([topicId]),
								onShowPurchase: handleShowPurchase,
								onViewItemTypeDetails: setDetailQuestionTypeId,
							},
						}}
					>
						<HomeApplet.Body />
					</HomeApplet>
				</ScrollContainer>
			</Box>
			{showBottomFab && (
				<BottomFab
					icon={"play-outline"}
					label={t("dashboard.header.quickStudy")}
					onPress={handleQuickStudy}
					bottom={16}
				/>
			)}
			{!isWeb && <AppReviewDialog />}
			{showFreeQuizDialog && (
				<FreeQuizSessionDialog
					visible={showFreeQuizDialog}
					onClose={() => {
						router.setParams({
							startDiagnostic: "false",
						});
					}}
				/>
			)}
			{!!olxError && (
				<OLXExamEligibilityDialog
					visible={!!olxError}
					handleClose={() => setOlxError?.(null)}
					title={olxError?.title ?? ""}
					message={olxError?.message ?? ""}
					onNavigateToPracticeTests={() => router.push("/practice-tests")}
				/>
			)}
			{showCollectUserDetailsDialog && toggleShowCollectUserDetailsDialog && (
				<CollectUserDetailsDialog
					visible={showCollectUserDetailsDialog}
					handleClose={toggleShowCollectUserDetailsDialog}
				/>
			)}
			<ItemTypeDetailModalContainer
				questionTypeId={detailQuestionTypeId}
				onClose={() => setDetailQuestionTypeId(undefined)}
				onStartRecommendedSession={startAdaptiveSession}
				starting={isCreatingAdaptiveSession}
			/>
			<SkillDetailDialogContainer
				skillId={detailSkillId}
				onClose={() => setDetailSkillId(undefined)}
				onStartPractice={(task) => startAdaptiveSession(task, "skill_detail")}
				startingPractice={isCreatingAdaptiveSession}
			/>
			{recommendedSessionDialog}
			<SetExamDateContainer
				showDatePicker={showDatePicker}
				toggleShowDatePicker={toggleShowDatePicker}
			/>
		</Box>
	);
};
